import {
  getChartData,
  getFpData,
  getFPBarChartData
} from '../../utils/barChartHelper'
import {
  compsMappingData,
  propertySearchDataToCompsMapping,
  numericFields,
  comparableNumericFields,
  propertyDataNumericFields,
  RENT_COMPARABLES,
  SALES_COMPARABLES,
  SH_COMPARABLES,
  propListHeader,
  rentUnitMixHeader,
  studentHousingUnitMixHeader
} from '@engine/dependencies/constants'
import moment from 'moment'
import { CompsError } from '@compsError'
import { distance } from '@engine/utils/jsUtils'

const replaceEmptyValue = obj => obj || 'NA'
const extractNumber = data => String(data).replace(/[$%,]/gi, '')
const sum = data =>
  data.reduce((accumulator, currentValue) => accumulator + currentValue, 0)
let unitMixHeader = rentUnitMixHeader
const average = (data, isRoundOff) => {
  if (!data.length) {
    return 0
  }

  return (sum(data) / data.length).toFixed(isRoundOff ? 0 : 2)
}

// TODO: Tagging
const weightedAverage = (values, isRoundOff) => {
  let weightedSum = 0
  let totalunits = 0
  if (!values.length) {
    return 0
  }
  values.forEach(currentValue => {
    weightedSum += currentValue.data * currentValue.unit
    totalunits += currentValue.unit
  })

  return isRoundOff
    ? (weightedSum / totalunits).toFixed(0)
    : (weightedSum / totalunits).toFixed(2)
}

const adjustChartData = (chartData, totalUnits) => {
  const adjustedData = {}
  let total = 0

  Object.keys(chartData).forEach(key => {
    const percentage = (chartData[key] / totalUnits) * 100
    adjustedData[key] = Math.round(percentage)
    total += adjustedData[key]
  })

  const difference = 100 - total

  if (difference !== 0 && Object.keys(adjustedData).length) {
    const largestKey = Object.keys(adjustedData).reduce((a, b) => {
      if (adjustedData[a] > adjustedData[b]) {

        return a
      }

      return b
    })
    adjustedData[largestKey] += difference
  }

  return adjustedData
}

const normalizePropertyData = worksheetData => {
  Object.keys(worksheetData).forEach(key => {
    if (key !== 'Summary' && key !== 'Map Data' && key !== 'Floor Plan') {
      const propertyObject = {}
      let isPropFlag = false
      let properties = {}
      propertyObject[key] = {
        comparableData: [],
        propertyData: {
          'Property Name': key,
          imageUrl: ''
        }
      }

      /* eslint max-depth: ['error', 6] */
      for (let j = 0; j < worksheetData[key].length; j += 1) {
        if (worksheetData[key][j].hasOwnProperty('__EMPTY')) {
          const propKeys = Object.keys(propertyObject[key].propertyData)
          const excludeEmptyFromList = Object.values(
            worksheetData[key][j]
          ).filter(item => item)
          if (
            Object.values(worksheetData[key][j]).some(prop =>
              propListHeader.includes(prop)
            ) &&
            !propKeys.some(propKey => excludeEmptyFromList.includes(propKey))
          ) {
            isPropFlag = true
          } else {
            isPropFlag = false
            if (Object.values(worksheetData[key][j]).includes('# BEDS')) {
              unitMixHeader = studentHousingUnitMixHeader
            }
            properties = createCompObject(Object.values(worksheetData[key][j]))
            if (Object.keys(properties).length > 1) {
              propertyObject[key].comparableData.push(properties)
            }
          }
        } else {
          isPropFlag = true
        }

        if (isPropFlag) {
          createPropDataObject(
            Object.values(worksheetData[key][j]),
            propertyObject[key].propertyData
          )
        }
      }
      unitMixHeader = rentUnitMixHeader
      worksheetData[key] = propertyObject[key]
    } else if (key === 'Summary') {
      worksheetData[key].forEach(obj => {
        obj.propertyType = Object.values(obj)[0] || ''
        Object.keys(obj).forEach(row => {
          obj[row] = replaceEmptyValue(obj[row])
        })
      })
    }
  })

  return worksheetData
}

const createCompObject = arr => {
  const propCompDataObj = {}
  for (let i = 0; i < unitMixHeader.length; i += 1) {
    if (unitMixHeader[i] !== arr[i]) {
      // added this tweak for saving compsdata to ES
      if (unitMixHeader[i] === 'DATE') {
        let date = moment(arr[i], 'MM/DD/YYYY').format('YYYY-MM-DD')
        date = date === 'Invalid date' ? null : date
        propCompDataObj[unitMixHeader[i]] = date
      } else {
        propCompDataObj[unitMixHeader[i]] = replaceEmptyValue(arr[i])
      }
    }
  }

  return propCompDataObj
}

const createPropDataObject = (arr, propObject) => {
  if (arr && arr.length > 2) {
    for (let k = 0, j = arr.length; k < j; k += 2) {
      if (arr[k] === '') {
        k += 1
      }
      setPropertyData(arr.slice(k, k + 2), propObject)
    }
  } else {
    setPropertyData(arr, propObject)
  }
}

const setPropertyData = (arr, propObject) => {
  const [key, value] = arr
  propObject[key] = replaceEmptyValue(value)
}

const updatePropertyData = (propertyData, imageData) => {
  if (imageData) {
    propertyData.Summary.forEach((obj, i) => {
      imageData.forEach(key => {
        if (key.property_code === obj.propertySFId) {
          propertyData.Summary[i].propertyData.imageUrl = key.signedUrl
          propertyData.Summary[i].signedUrl = key.signedUrl
        }
      })
    })
  }

  return propertyData.Summary
}

const normalizeWorkbookData = worksheetData => {
  let chartDataByComparableData = []
  const propertyInfo = normalizePropertyData(worksheetData)
  const subjectProp = propertyInfo.Summary && propertyInfo.Summary[0]
  const templateName = subjectProp.hasOwnProperty('Sale Date')
    ? SALES_COMPARABLES
    : subjectProp.hasOwnProperty('# Beds')
    ? SH_COMPARABLES
    : RENT_COMPARABLES

  propertyInfo.Summary.map(summaryObj => {
    if (propertyInfo[summaryObj['Property Name']]) {
      // propertyInfo[summaryObj['Property Name']].comparableData.splice(0, 2)


      if (propertyInfo[summaryObj['Property Name']]?.comparableData?.length > 2) {
        propertyInfo[summaryObj['Property Name']].comparableData = [
            ...propertyInfo[summaryObj['Property Name']].comparableData.filter(
              (comparable, index) => (comparable.SOURCE === 'NA'
                  && index !== (propertyInfo[summaryObj['Property Name']].comparableData.length - 1) ?
                    undefined : comparable
              ))
          ]
      } else {
        propertyInfo[summaryObj['Property Name']].comparableData.splice(0, 2)
      }

      if (templateName !== SALES_COMPARABLES) {
        chartDataByComparableData = JSON.parse(
          JSON.stringify(
            propertyInfo[summaryObj['Property Name']].comparableData.slice(
              0,
              19
            )
          )
        )
      } else {
        chartDataByComparableData = JSON.parse(
          JSON.stringify(
            propertyInfo[summaryObj['Property Name']].comparableData
          )
        )
      }

      const chartData = chartDataByComparableData.reduce((map, obj) => {
        if (obj['UNIT TYPE'] !== '(total bed/bath)') {
          if (obj['UNIT TYPE'].includes('0 Bedroom')) {
            obj['UNIT TYPE'] = '0 Bedroom /  1 Bath'
          }
          const chartDataKey = obj['# BEDS'] ? '# BEDS' : '#UNITS'
          if (map.hasOwnProperty(obj['UNIT TYPE'])) {
            map[obj['UNIT TYPE']] =
              Number(map[obj['UNIT TYPE']]) + Number(obj[chartDataKey])
          } else if (obj[chartDataKey] !== 'NA') {
            map[obj['UNIT TYPE']] = Number(obj[chartDataKey])
          }
        }
        delete map.NA

        return map
      }, {})
      const totalUnits =
        chartData &&
        Object.values(chartData).reduce((total, value) => total + value, 0)
      if (chartData) {
        let chartDataObj = {}
        Object.keys(chartData).forEach(key => {
          // chartData[key] = Math.round((chartData[key] / totalUnits) * 100)
          const updatedKey = key.replace(/\s/g, '').toLowerCase()
            .replace('/', ' / ')
            .replace('bedroom', ' Bedroom')
            .replace('bath', ' Bath')
          if (!isNaN(chartData[key])) {
            chartDataObj[updatedKey] = chartData[key]
          }
        })

        chartDataObj = adjustChartData(chartDataObj, totalUnits)

        propertyInfo[summaryObj['Property Name']].chartData = chartDataObj
      }

      propertyInfo[summaryObj['Property Name']].propertyData.propertyType =
        summaryObj.propertyType
      propertyInfo[summaryObj['Property Name']].propertyData.Units =
        summaryObj.Units
      if (summaryObj['# Beds']) {
        propertyInfo[summaryObj['Property Name']].propertyData['# BEDS'] =
          summaryObj['# Beds']
      }
      propertyInfo[summaryObj['Property Name']].propertyData.Distance =
        propertyInfo[summaryObj['Property Name']].propertyData.Distance ===
          '0 miles'
          ? 'NA'
          : propertyInfo[summaryObj['Property Name']].propertyData.Distance
      if (
        propertyInfo[summaryObj['Property Name']].propertyData.propertyType
          .toString()
          .includes('S')
      ) {
        summaryObj.Distance = ''
        propertyInfo[summaryObj['Property Name']].propertyData.Distance = ''
      }
      const amenities =
        propertyInfo[summaryObj['Property Name']].propertyData.Amenities
      const notes =
        propertyInfo[summaryObj['Property Name']].propertyData['Rent Comments']
      const sourcenotes =
        propertyInfo[summaryObj['Property Name']].propertyData['Source Notes']

      propertyInfo[summaryObj['Property Name']].propertyData.Amenities =
        amenities === 'NA'
          ? []
          : amenities.substring(0, amenities.length - 1).split(';')
      propertyInfo[summaryObj['Property Name']].propertyData.Notes =
        notes === 'NA' ? '' : notes.trim()
      propertyInfo[summaryObj['Property Name']].propertyData['Source Notes'] =
        sourcenotes === 'NA' ? '' : sourcenotes.trim()
      propertyInfo[summaryObj['Property Name']].propertyData.RSF =
        summaryObj.RSF
      propertyInfo[summaryObj['Property Name']].propertyData.avgMarketRentSf =
        summaryObj['Avg Market Rent'] ?? summaryObj['Avg Market  Rent']
      propertyInfo[summaryObj['Property Name']].propertyData.avgRentSf =
        summaryObj['Avg Rent/SF']
      propertyInfo[summaryObj['Property Name']].propertyData.averageSf =
        summaryObj['Avg SF']
      summaryObj.State =
        propertyInfo[summaryObj['Property Name']].propertyData.State

      propertyInfo['Map Data'].map(obj => {
        if (summaryObj['Property Name'] === obj['Property Name']) {
          summaryObj['Property SF ID'] = obj['Property SF ID']
          summaryObj.propertyAddressLatitude = obj.Latitude
          summaryObj.propertyAddressLongitude = obj.Longitude
        }

        return null
      })

      summaryObj.propertyData =
        propertyInfo[summaryObj['Property Name']].propertyData
      summaryObj.comparableData =
        propertyInfo[summaryObj['Property Name']].comparableData
      summaryObj.chartData = propertyInfo[summaryObj['Property Name']].chartData
      // summaryObj.colorCodesForChart = propertyInfo[summaryObj['Property Name']].colorCodesForChart
    } else if (summaryObj['Property Name'] !== 'NA') {
      throw new CompsError(
        'Mismatch of property name between Summary sheet and Individual Property'
      )
    }

    return null
  })

  propertyInfo.Summary[propertyInfo.Summary.length - 1].State = 'NA'

  return mapppingCompsData(propertyInfo, templateName)
}

const objectWithExtractedNumbers = (dataObj, fields) => {
  // Modifying dataObj in-place
  fields.forEach(field => {
    if (dataObj.hasOwnProperty(field)) {
      const valueAsNumber = dataObj[field] && extractNumber(dataObj[field])
      dataObj[field] =
        valueAsNumber === 'NA' || !valueAsNumber ? null : Number(valueAsNumber)
    }
  })
}

const formatToNumbers = propertiesData => {
  const updatedPropertiesData = JSON.parse(JSON.stringify(propertiesData))

  updatedPropertiesData.forEach(property => {
    objectWithExtractedNumbers(property, numericFields)
    property.comparableData.forEach(comparable => {
      objectWithExtractedNumbers(comparable, comparableNumericFields)
    })
    objectWithExtractedNumbers(property.propertyData, propertyDataNumericFields)
  })

  return updatedPropertiesData
}
const calculateTotals = (propertiesData, templateName) => {
  const updatedPropertiesData = JSON.parse(JSON.stringify(propertiesData))
  const shAvgBeds = []

  updatedPropertiesData.forEach(property => {
    if (
      property.propertyType !== 'NA' &&
      !property.propertyType
      .toString()
      .toLowerCase()
      .includes('s')
    ) {
      if (templateName?.includes(SH_COMPARABLES)) {
        if (property.beds) {
          shAvgBeds.push(Number(property.beds))
        }
      }
    }

    return property
  })

  if (templateName?.includes(SH_COMPARABLES)) {
    updatedPropertiesData[updatedPropertiesData.length - 1].beds = Number(
      average(shAvgBeds, true)
    )
  }

  return updatedPropertiesData
}

// Mapping excel comps data to projects db schema
// to support and load the schema to preview screen
const mapppingCompsData = (propertyInfo, templateName) => {
  const projectsData = []
  propertyInfo.Summary.forEach((prop, i) => {
    projectsData[i] = {}
    projectsData[i].chartData = prop.chartData || null
    // projectsData[i].colorCodesForChart = prop.colorCodesForChart || null
    projectsData[i].comparableData = []
    projectsData[i].propertyData = {}

    // summary data mapping
    compsMappingData.summaryData.forEach(attr => {
      // added this tweak for saving compsdata to ES
      if (
        (attr.key === 'date' && attr.value === 'Updated') ||
        (attr.key === 'mostRecentSaleDate' && attr.value === 'Sale Date')
      ) {
        const date = moment(prop[attr.value], 'MM/DD/YYYY').format('YYYY-MM-DD')
        projectsData[i][attr.key] = date === 'Invalid date' ? null : date
      } else if (attr.key === 'propertyAddress' && attr.value === 'Distance') {
        const propertyAddress = prop[attr.value]
        projectsData[i][attr.key] =
          propertyAddress === 'NA' ? '' : propertyAddress
      } else {
        projectsData[i][attr.key] = prop[attr.value] ?? prop[attr.fallbackValue]
      }
    })

    // property data mapping
    if (prop.propertyData) {
      compsMappingData.propertyData.forEach(attr => {
        if (attr.key === 'mostRecentSaleDate' && attr.value === 'Sale Date') {
          const date = moment(prop[attr.value], 'MM/DD/YYYY').format(
            'YYYY-MM-DD'
          )
          projectsData[i].propertyData[attr.key] =
            date === 'Invalid date' ? null : date
        } else {
          projectsData[i].propertyData[attr.key] = prop.propertyData[attr.value]
        }
      })
    }

    // comparable data mapping
    if (prop.comparableData) {
      prop.comparableData.forEach(comp => {
        const compObj = {}
        compsMappingData.comparableData.forEach(attr => {
          if (attr.key === 'unitType') {
            comp[attr.value] = comp[attr.value]
              .replace(/\.00/g, '')
              .replace(/\.50/g, '.5')
          }
          compObj[attr.key] = comp[attr.value]
        })
        projectsData[i].comparableData.push(compObj)
        objectWithExtractedNumbers(compObj, comparableNumericFields)
      })

      if (
        templateName !== SALES_COMPARABLES &&
        projectsData[i].comparableData.length > 20
      ) {
        projectsData[i].comparableData = calculateWtdAvgRowForUnitMix(
          projectsData[i].comparableData
        )
      }
    }
  })

  propertyInfo.Summary = formatToNumbers(projectsData)
  propertyInfo.Summary = calculateTotals(propertyInfo.Summary, templateName)
  propertyInfo.barChartData = getChartData(propertyInfo.Summary, templateName)
  propertyInfo.templateName = templateName
  if (templateName === RENT_COMPARABLES || templateName === SH_COMPARABLES) {
    propertyInfo.fpSummaryData = getFpData(propertyInfo.Summary)
    propertyInfo.fpBarChartData = getFPBarChartData(propertyInfo.fpSummaryData)
  }

  return propertyInfo
}

const normalizePropertyDeleted = (state, deletedProperty) => {
  const propertyData = state.compsData
  const { templateName, barChartSettings } = state
  const rentUnits = []
  const rentOccupancy = []
  const rentAvgSF = []
  const rentAvgMarketRent = []
  const rentAvgRentPerSF = []
  const shAvgBeds = []
  const saleUnits = []
  const salePrice = []
  const salePricePerUnit = []
  const salePricePerSF = []
  const saleCAP = []

  const properties = deletedProperty
    ? propertyData.filter(
      property => property.propertyType !== deletedProperty.propertyType
    )
    : propertyData

  const updatedProperties = properties.map((property, index) => {
    if (
      property.propertyType !== 'NA' &&
      !property.propertyType
        .toString()
        .toLowerCase()
        .includes('s')
    ) {
      if (property.propertyType > deletedProperty?.propertyType) {
        property.propertyType -= 1
        property.propertyData.propertyType -= 1
      }

      if (
        templateName?.includes(RENT_COMPARABLES) ||
        templateName?.includes(SH_COMPARABLES)
      ) {
        if (property.units) {
          rentUnits.push(Number(property.units))
        }
        if (property.occupancyAsOfDate) {
          rentOccupancy.push(Number(property.occupancyAsOfDate))
        }
        if (property.avgSf) {
          rentAvgSF.push({
            data: property.avgSf,
            unit: Number(property.units)
          })
        }
        if (property.avgMarketRentSf) {
          rentAvgMarketRent.push({
            data: property.avgMarketRentSf,
            unit: Number(property.units)
          })
        }
        if (property.avgRentSf) {
          rentAvgRentPerSF.push({
            data: property.avgRentSf,
            unit: Number(property.units)
          })
        }
        if (Number(property.beds?.toString().replace(/,/g, ''))) {
          shAvgBeds.push(Number(property.beds?.toString().replace(/,/g, '')))
        }
      }
      if (templateName === SALES_COMPARABLES) {
        if (property.units) {
          saleUnits.push(Number(property.units))
        }
        if (property.mostRecentSalePrice) {
          salePrice.push({
            data: property.mostRecentSalePrice,
            unit: Number(property.units)
          })
        }
        if (property.priceUnit) {
          salePricePerUnit.push({
            data: property.priceUnit,
            unit: Number(property.units)
          })
        }
        if (property.priceSf) {
          salePricePerSF.push({
            data: property.priceSf,
            unit: Number(property.units)
          })
        }
        if (property.capRate) {
          saleCAP.push({
            data: property.capRate,
            unit: Number(property.units)
          })
        }
      }
    } else if (
      property.propertyType
        .toString()
        .toLowerCase()
        .includes('s') &&
      property.propertyType.toString().length > 1
    ) {
      property.propertyType = `S${index + 1}`
      property.propertyData.propertyType = `S${index + 1}`
    }

    return property
  })

  if (
    templateName?.includes(RENT_COMPARABLES) ||
    templateName?.includes(SH_COMPARABLES)
  ) {
    updatedProperties[updatedProperties.length - 1].units = Number(
      sum(rentUnits)
    )
    updatedProperties[updatedProperties.length - 1].occupancyAsOfDate = Number(
      average(rentOccupancy, true)
    )
    updatedProperties[updatedProperties.length - 1].avgSf = Number(
      weightedAverage(rentAvgSF, true)
    )
    updatedProperties[updatedProperties.length - 1].avgMarketRentSf = Number(
      weightedAverage(rentAvgMarketRent, true)
    )
    updatedProperties[updatedProperties.length - 1].avgRentSf = Number(
      weightedAverage(rentAvgRentPerSF)
    )
  }
  if (templateName?.includes(SH_COMPARABLES)) {
    updatedProperties[updatedProperties.length - 1].beds = Number(
      average(shAvgBeds, true)
    )
  }
  if (templateName === SALES_COMPARABLES) {
    updatedProperties[updatedProperties.length - 1].units = Number(
      sum(saleUnits)
    )
    updatedProperties[
      updatedProperties.length - 1
    ].mostRecentSalePrice = Number(weightedAverage(salePrice, true))
    updatedProperties[updatedProperties.length - 1].priceUnit = Number(
      weightedAverage(salePricePerUnit, true)
    )
    updatedProperties[updatedProperties.length - 1].priceSf = Number(
      weightedAverage(salePricePerSF)
    )
    updatedProperties[updatedProperties.length - 1].capRate = Number(
      weightedAverage(saleCAP)
    )
  }
  const compsData = {
    updatedProperties
  }

  const barChartData = getChartData(
    updatedProperties,
    templateName,
    barChartSettings
  )
  compsData.barChartData = barChartData
  if (
    templateName?.includes(RENT_COMPARABLES) ||
    templateName?.includes(SH_COMPARABLES)
  ) {
    compsData.fpSummaryData = getFpData(updatedProperties)
    compsData.fpBarChartData = getFPBarChartData(
      compsData.fpSummaryData,
      barChartSettings
    )
  }

  return compsData
}

const calculateWtdAvgRowForUnitMix = (
  unitMixArr,
  unitMixLimit = 19,
  isWtdAvgRowExists = true,
  templateType = 'SH1'
) => {
  const wtdAvgrow = {
    date: null,
    source: 'NA',
    comments: 'NA',
    unitType: 'NA'
  }
  const activeUnitMixArr = unitMixArr.filter(
    unitMix => unitMix.isRemoved !== true
  )

  // Update RENT/SF and EFF RENT/SF for any change(s) in Unit SF, Market Rent and/or Eff Rent
  const lastRecordIndexExcludingWtAvgRow = activeUnitMixArr.length - 2
  activeUnitMixArr.forEach((rowObj, index) => {
    if (index <= lastRecordIndexExcludingWtAvgRow) {
      rowObj.mrPerSf = rowObj.unitMarketRent && Number(rowObj.unitMarketRent / rowObj.unitSize).toFixed(2)
      rowObj.erPerSf = rowObj.unitEffectiveRent && Number(rowObj.unitEffectiveRent / rowObj.unitSize).toFixed(
        2
      )
      if (templateType === 'SH1' && rowObj.hasOwnProperty('beds')) {
        const unitTypeBedNumber = rowObj.unitType.split(' ')[0] === 'NA' ? null : Number(rowObj.unitType.split(' ')[0])
        rowObj.erPerBed = Number(unitTypeBedNumber === 0 ? rowObj.unitMarketRent : Number(rowObj.unitMarketRent / unitTypeBedNumber))
      }
    }
  })

  const unitMixObj = JSON.parse(
    JSON.stringify(
      activeUnitMixArr.slice(
        0,
        unitMixLimit === 0 ? activeUnitMixArr.length : unitMixLimit
      )
    )
  )
  if (
    unitMixObj &&
    unitMixObj[unitMixObj.length - 1].source === 'NA' &&
    unitMixObj[unitMixObj.length - 1].unitType === 'NA'
  ) {
    unitMixObj.pop()
  }

  wtdAvgrow.units = sumUnits(unitMixObj, 'units')
  wtdAvgrow.unitSize = getWeightedAverage(unitMixObj, 'unitSize', 0)
  wtdAvgrow.totalSize = sumUnits(unitMixObj, 'totalSize')
  wtdAvgrow.unitMarketRent = getWeightedAverage(unitMixObj, 'unitMarketRent', 0)
  wtdAvgrow.unitEffectiveRent = getWeightedAverage(
    unitMixObj,
    'unitEffectiveRent',
    0
  )
  wtdAvgrow.erPerSf = getWeightedAverage(unitMixObj, 'erPerSf', 2)
  wtdAvgrow.mrPerSf = Number(
    (wtdAvgrow.unitMarketRent / wtdAvgrow.unitSize).toFixed(2)
  )
  wtdAvgrow.totalMr = getWeightedAverage(unitMixObj, 'totalMr', 0)
  wtdAvgrow.totalEr = getWeightedAverage(unitMixObj, 'totalEr', 0)
  if (templateType === 'SH1' && unitMixObj[0]?.hasOwnProperty('beds')) {
    wtdAvgrow.beds = sumUnits(unitMixObj, 'beds')
    wtdAvgrow.bedsPerunit = getWeightedAverage(unitMixObj, 'bedsPerunit', 2, 'beds')
    wtdAvgrow.mrPerBed = getWeightedAverage(unitMixObj, 'mrPerBed', 0, 'beds')
    wtdAvgrow.erPerBed = getWeightedAverage(unitMixObj, 'erPerBed', 0, 'beds')
  }

  if (isWtdAvgRowExists) {
    activeUnitMixArr[activeUnitMixArr.length - 1] = wtdAvgrow
  } else {
    activeUnitMixArr[activeUnitMixArr.length] = wtdAvgrow
  }

  return activeUnitMixArr
}

const calculateDonutChartData = (unitMixArr, unitMixLimit = 19, templateName) => {
  const unitMixObj = JSON.parse(
    JSON.stringify(
      unitMixArr.slice(0, unitMixLimit === 0 ? unitMixArr.length : unitMixLimit)
    )
  )
  let chartData = unitMixObj.reduce((map, obj) => {
    if (obj.unitType !== '(total bed/bath)') {
      if (obj.unitType.includes('0 Bedroom')) {
        obj.unitType = '0 Bedroom /  1 Bath'
      }
      const chartDataKey = templateName?.includes('studentHousingComps') ? 'beds' : 'units'
      if (map.hasOwnProperty(obj.unitType)) {
        map[obj.unitType] =
          Number(map[obj.unitType]) + Number(obj[chartDataKey])
      } else {
        map[obj.unitType] = Number(obj[chartDataKey])
      }
    }
    delete map.NA

    return map
  }, {})
  const totalUnits =
    chartData &&
    Object.values(chartData).reduce((total, value) => total + value, 0)
  if (chartData) {
    // Object.keys(chartData).forEach(key => {
    //   chartData[key] = Math.round((chartData[key] / totalUnits) * 100)
    // })
    chartData = adjustChartData(chartData, totalUnits)
  }

  if (Object.keys(chartData).length !== 0) {
    const chartDataObj = {}
    Object.keys(chartData).forEach(key => {
      const updatedKey = key.replace(/\s/g, '').toLowerCase()
        .replace('/', ' / ')
        .replace(/\.00/g, '')
        .replace(/\.50/g, '.5')
        .replace('bedroom', ' Bedroom')
        .replace('bath', ' Bath')
      if (!isNaN(chartData[key])) {
        chartDataObj[updatedKey] = chartData[key]
      }
    })
    chartData = chartDataObj
  }

  return chartData
}

const sumUnits = (arr, key) => arr.reduce((prev, cur) => prev + cur[key], 0)
/* eslint max-params: [1, 4] */
const getWeightedAverage = (values, key, roundOff, totalKey = 'units') => {
  let weightedSum = 0
  let totalUnits = 0

  values.forEach(currentValue => {
    if (currentValue[key] !== null) {
      weightedSum += currentValue[key] * currentValue[totalKey]
      totalUnits += currentValue[totalKey]
    }
  })

  return Number((weightedSum / totalUnits).toFixed(roundOff))
}

const mappingPropertyDataToComps = (state, source) => {
  const { propertySearchData, templateDataLimits, compsData } = state
  const propertyToAdd = {
    comparableData: [],
    propertyData: {},
    chartData: {}
  }
  let unitMixRecords = []
  let unitMixCountAll = 19
  if (source && propertySearchData?.omniviewpropertyrent) {
    unitMixRecords = [
      propertySearchData.omniviewpropertyrent?.filter(
        x => x.source.toLowerCase() === source.toLowerCase()
      )
    ]
    unitMixRecords[0] = [...sortUnitType(unitMixRecords[0])]
  } else {
    unitMixRecords = [propertySearchData.omniviewpropertyrent]
    unitMixRecords[0] = unitMixRecords[0] && [...sortUnitType(unitMixRecords[0])]
  }

  if (templateDataLimits.templateName.includes(SALES_COMPARABLES)) {
    unitMixCountAll = 0
  } else {
    unitMixCountAll = templateDataLimits.unitMixCount
  }

  /* eslint-disable no-unused-expressions */
  unitMixRecords[0]?.forEach(comp => {
    const compObj = {}
    propertySearchDataToCompsMapping.comparableData.forEach(attr => {
      if (attr.key === 'unitType') {
        comp[attr.value] = comp[attr.value].trim().replace(/ /g, '')
          .toLowerCase()
          .replace('/', ' / ')
          .replace(/\.00/g, '')
          .replace(/\.50/g, '.5')
          .replace('bedroom', ' Bedroom')
          .replace('bath', ' Bath')
      }
      compObj[attr.key] = replaceEmptyValue(comp[attr.value])
    })
    objectWithExtractedNumbers(compObj, comparableNumericFields)
    propertyToAdd.comparableData.push(compObj)
  })

  if (propertyToAdd.comparableData?.length > 0) {
    propertyToAdd.comparableData = calculateWtdAvgRowForUnitMix(propertyToAdd.comparableData, unitMixCountAll, false)
    propertyToAdd.chartData = calculateDonutChartData(propertyToAdd.comparableData, unitMixCountAll, templateDataLimits.templateName)
    propertySearchDataToCompsMapping.fromComparableDataLastRow.forEach(attr => {
      propertyToAdd.propertyData[attr.key] = replaceEmptyValue(
        propertyToAdd.comparableData[propertyToAdd.comparableData.length - 1][
        attr.value
        ]
      )
    })
  }

  propertySearchDataToCompsMapping.propertyData.forEach(attr => {
    propertyToAdd.propertyData[attr.key] = replaceEmptyValue(propertySearchData.omniviewproperty?.[0][attr.value])
  })

  if (unitMixRecords[0]?.[0]) {
    propertySearchDataToCompsMapping.fromComparableData.forEach(attr => {
      propertyToAdd.propertyData[attr.key] = replaceEmptyValue(
        unitMixRecords[0][0][attr.value]
      )
    })
  }

  propertyToAdd.propertySFId = propertySearchData.omniViewPropertyId
  let lstOmniviewtransactionhistory = propertySearchData.omniviewtransactionhistory?.filter(x => x.transactionType?.toLowerCase() === 'sale')
  lstOmniviewtransactionhistory = lstOmniviewtransactionhistory ? [...sortArray(lstOmniviewtransactionhistory, 'saleDate', 'desc')] : []
  const propertySaleData = lstOmniviewtransactionhistory.length ? lstOmniviewtransactionhistory[0] : {}

  propertySearchDataToCompsMapping.fromOmniviewtransactionhistory.forEach(attr => {
    if (attr.key === 'mostRecentSaleDate') {
      const date = moment(propertySaleData[attr.value], 'YYYY-MM-DD').format('YYYY-MM-DD')
      propertyToAdd.propertyData[attr.key] = date === 'Invalid date' ? null : date
    } else {
      propertyToAdd.propertyData[attr.key] = replaceEmptyValue(propertySaleData[attr.value])
    }
  })

  propertySearchDataToCompsMapping.summaryData.forEach(attr => {
    if (attr.key === 'mostRecentSaleDate') {
      propertyToAdd[attr.key] = propertyToAdd.propertyData[attr.value]
    } else {
      propertyToAdd[attr.key] = replaceEmptyValue(
        propertyToAdd.propertyData[attr.value]
      )
    }
  })

  propertyToAdd.propertyData.amenities = (propertyToAdd.propertyData.amenities && propertyToAdd.propertyData.amenities !== 'NA') ? propertyToAdd.propertyData.amenities.substring(0, propertyToAdd.propertyData.amenities.length - 1).split(';') : []
  propertyToAdd.propertyData.notes = propertyToAdd.propertyData.notes === 'NA' ? '' : propertyToAdd.propertyData.notes
  propertyToAdd.propertyData.sourceNote = (propertyToAdd.propertyData?.sourceNote === undefined || propertyToAdd.propertyData.sourceNote === 'NA') ? '' : propertyToAdd.propertyData.sourceNote
  propertyToAdd.date = propertyToAdd.date === 'NA' ? null : propertyToAdd.date
  if (compsData?.[0]?.propertyType.toString().includes('S') && compsData[0].propertyAddressLongitude && compsData[0].propertyAddressLatitude) {
    const propertyAddress = distance(compsData[0].propertyAddressLongitude, compsData[0].propertyAddressLatitude, propertyToAdd.propertyAddressLongitude, propertyToAdd.propertyAddressLatitude)
    propertyToAdd.propertyAddress = propertyAddress
    propertyToAdd.propertyData.propertyAddress = `${propertyAddress} miles`
  }
  if (propertySearchData?.signedUrl) {
    propertyToAdd.signedUrl = propertySearchData.signedUrl
    propertyToAdd.propertyData.imageUrl = propertySearchData.signedUrl
  }
  // formatting data
  objectWithExtractedNumbers(propertyToAdd, numericFields)
  /* eslint-disable no-unused-expressions */
  propertyToAdd.comparableData?.forEach(comparable => {
    objectWithExtractedNumbers(comparable, comparableNumericFields)
  })
  objectWithExtractedNumbers(
    propertyToAdd.propertyData,
    propertyDataNumericFields
  )

  return propertyToAdd
}

function sortArray(array, sortElement, direction) {
  return array.sort((a, b) => {
    const sortElementA = a[sortElement]
    const sortElementB = b[sortElement]

    if (direction === 'asc') {
      if (sortElementA < sortElementB) {
        return -1
      }
      if (sortElementA > sortElementB) {
        return 1
      }
    }
    if (direction === 'desc') {
      if (sortElementA > sortElementB) {
        return -1
      }
      if (sortElementA < sortElementB) {
        return 1
      }
    }

    return 0
  })
}

function sortUnitType(arr) {
  return arr?.sort((a, b) => {
    if (a.unitType?.toLowerCase() === 'studio') {
      a.unitType = '0 Bedroom/1 Bath'
    }
    if (b.unitType?.toLowerCase() === 'studio') {
      b.unitType = '0 Bedroom/1 Bath'
    }

    if (a.unitType < b.unitType) {
      return -1
    }
    if (a.unitType > b.unitType) {
      return 1
    }
    if (a.unitSize < b.unitSize) {
      return -1
    }
    if (a.unitSize > b.unitSize) {
      return 1
    }

    return 0
  })
}

const getUnitMixSource = unitMixRecords => {
  let source = ''
  const BCM = unitMixRecords.filter(val => val.source === 'BCM')
  const Axio = unitMixRecords.filter(val => val.source === 'Axio')
  const Yardi = unitMixRecords.filter(val => val.source === 'Yardi')

  let tempBCM = BCM.length > 0 ? sortArray(BCM, 'date', 'desc')[0].date : '1800-01-01'
  let tempAxio = Axio.length > 0 ? sortArray(Axio, 'date', 'desc')[0].date : '1800-01-01'
  let tempYardi = Yardi.length > 0 ? sortArray(Yardi, 'date', 'desc')[0].date : '1800-01-01'

  tempBCM = tempBCM === '' ? '1800-01-01' : tempBCM
  tempAxio = tempAxio === '' ? '1800-01-01' : tempAxio
  tempYardi = tempYardi === '' ? '1800-01-01' : tempYardi

  if (tempBCM >= tempAxio && tempBCM >= tempYardi) {
    source = 'BCM'
  } else if (tempAxio >= tempYardi) {
    source = 'Axio'
  } else if (tempYardi !== '1800-01-01' && tempYardi > tempAxio && tempYardi > tempBCM) {
    source = 'Yardi'
  }

  return source
}

const updateListInfo = (listInfo, imageData) => {
  if (imageData) {
    listInfo.compsList.forEach((obj, i) => {
      imageData.forEach(key => {
        if (key.property_code === obj.omniViewPropertyId) {
          listInfo.compsList[i].imageUrl = key.signedUrl
        }
      })
    })
  }

  return listInfo
}

const createCompsDataFromOVList = (listInfo, templateDataLimitss) => {
  const compsData = []
  let subPropLong, subPropLot
  let source = ''
  let subProp = 0
  let comProp = 0
  const compsList = listInfo?.compsList
  compsList.sort((obj1, obj2) => obj1.rank - obj2.rank)
  compsList.sort(
    (obj1, obj2) => obj2.isSubjectProperty - obj1.isSubjectProperty
  )

  compsList.forEach(compsRow => {
    if (compsRow?.data?.omniViewPropertyId) {
      // unitmix source
      if (compsRow?.sourcePreference) {
        source = compsRow?.sourcePreference
      } else if (compsRow.data?.omniviewpropertyrent?.length > 0) {
        source = getUnitMixSource(compsRow.data.omniviewpropertyrent)
      }

      const propertySearchData = mappingPropertyDataToComps(
        {
          templateDataLimits: templateDataLimitss,
          propertySearchData: compsRow.data
        },
        source
      )
      if (compsRow?.isSubjectProperty && compsRow?.rank) {
        propertySearchData.pType = 'subject'
        subProp += 1
      } else {
        propertySearchData.pType = 'comparable'
        comProp += 1
      }

      // property image
      if (
        compsRow?.omniViewPropertyId === propertySearchData.propertySFId &&
        compsRow?.imageUrl
      ) {
        propertySearchData.propertyData.imageUrl = compsRow.imageUrl
        propertySearchData.signedUrl = compsRow.imageUrl
      }

      compsData.push(propertySearchData)
    }
  })
  compsData.push({
    propertyType: 'NA',
    notes: 'NA',
    name: 'NA',
    comparableData: [],
    propertyData: {},
    chartData: {},
    mostRecentSaleDate: null,
    city: 'NA',
    propertyAddress: null,
    yearBuilt: null,
    yearRen: null,
    state: 'NA',
    occupancyAsOfDate: null
  })

  compsData.map((comp, index) => {
    // propertyType generation
    if (subProp === 1 && comp.pType === 'subject') {
      comp.propertyType = 'S'
      comp.propertyData.propertyType = 'S'
      subPropLong = comp.propertyAddressLongitude
      subPropLot = comp.propertyAddressLatitude
    } else if (subProp > 1 && comp.pType === 'subject') {
      comp.propertyType = `S${index + 1}`
      comp.propertyData.propertyType = `S${index + 1}`
      if (comp.propertyType === 'S1') {
        subPropLong = comp.propertyAddressLongitude
        subPropLot = comp.propertyAddressLatitude
      }
    } else if (comProp >= 1 && subProp >= 1 && comp.pType === 'comparable') {
      comp.propertyType = index - subProp + 1
      comp.propertyData.propertyType = index - subProp + 1
    } else if (subProp === 1 && comProp > 1 && comp.pType === 'comparable') {
      comp.propertyType = index
      comp.propertyData.propertyType = index
    } else if (subProp === 0 && comProp > 1 && comp.pType === 'comparable') {
      comp.propertyType = index + 1
      comp.propertyData.propertyType = index + 1
    }

    // distance calculation
    if (comp.pType === 'comparable' && subPropLong && subPropLot) {
      const propertyAddress = distance(
        subPropLong,
        subPropLot,
        comp.propertyAddressLongitude,
        comp.propertyAddressLatitude
      )
      comp.propertyAddress = propertyAddress
      comp.propertyData.propertyAddress = `${propertyAddress} miles`
    }

    return comp
  })

  return compsData
}

export {
  normalizeWorkbookData,
  normalizePropertyData,
  createCompObject,
  createPropDataObject,
  setPropertyData,
  updatePropertyData,
  normalizePropertyDeleted,
  calculateWtdAvgRowForUnitMix,
  calculateDonutChartData,
  mappingPropertyDataToComps,
  getUnitMixSource,
  updateListInfo,
  createCompsDataFromOVList
}
